import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { Direction } from 'radix-ui';
import { LucideProvider } from 'lucide-react';

import { router } from './app/router';
import { queryClient } from './lib/api';
import { initPalette } from './lib/palette';
import { applyDarkTheme } from './lib/theme';
import { installLatinNumeralObserver } from './lib/latin-numerals';
import './styles/app.css';

/**
 * الإقلاع. ترتيب المزودات مقصود:
 * Query (مخزن حالة الخادم الوحيد) ← Direction rtl (كل مكونات Radix تحته — §3.1-1)
 * ← LucideProvider (20px وسماكة 1.75 لكل أيقونة — §1.1) ← Router.
 */
/* سكربت ما قبل الرسم في index.html طبّق المحورين أصلًا؛ هذان النداءان يضمنان
 * تطابق الـ DOM مع التخزين ويشتركان في مراقبة تفضيل النظام (D76). */
applyDarkTheme();
initPalette();

const container = document.getElementById('root');
if (container === null) throw new Error('عنصر #root غير موجود في index.html');
/* Radix portals تُركّب خارج #root، لذلك حدّ العرض يراقب body كاملًا قبل أول تصيير. */
installLatinNumeralObserver(document.body);

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <Direction.DirectionProvider dir="rtl">
        {/*
         * **16px بسماكة 2 — لا 20 بسماكة 1.75.** أمر المالك 2026-08-11:
         * «شوف حجم الأيقونات والخط كيف صغير ومريح» في bestversion، ثم
         * «الـUI لازال بعيدًا… إمّا أن تطابق كليًّا».
         *
         * والقيم **مقيسة من مصدره لا مقدَّرة**: أيقوناته كلها SVG سطريّة
         * (‏74 عنصرًا)، رمز التنقّل `width/height: 18, strokeWidth: 2`
         * ورمز المحتوى `14, strokeWidth: 2` — **الوسيط 13px والسماكة 2
         * في 26 من 52 تصريحًا**. وافتراضُنا 20px كان يرثه 49 من 381 أيقونة
         * في فلو، فتخرج كل أيقونةٍ لم يُطلب مقاسها **أكبر بنحو الثلث** من
         * أكبر رمزٍ روتينيّ عنده.
         *
         * و16 لا 13: الوسيط عنده يشمل رموزًا داخل نصٍّ صغير، وأكثر مواضع
         * فلو التي ترث الافتراضي أزرارٌ وترويسات — فـ16 بينهما، ومن يحتاج
         * أصغر يطلبه صراحةً كما تفعل المكوّنات اليوم.
         *
         * **والسماكة 2 ترفع الوضوح عند التصغير**: خطٌّ 1.75 على 16px يبهت،
         * وهو ما يجعل الأيقونة الصغيرة تُقرأ رماديةً لا حادّة.
         */}
        <LucideProvider size={16} strokeWidth={2}>
          <RouterProvider router={router} />
        </LucideProvider>
      </Direction.DirectionProvider>
    </QueryClientProvider>
  </StrictMode>,
);
